import React, { useState, useEffect, useCallback } from 'react';
import { FaHistory, FaSyncAlt, FaSearch } from 'react-icons/fa';
import { crmDashboardApi } from '../../api/crmApi';
import AppDropdown from '../../components/AppDropdown';
import { ActivityTimeline } from './components/ActivityTimeline';
import './CRM.css';

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

const fallbackActivities = [
  { id: 1, type: 'Call', title: 'Call with Ankit Mehta', desc: 'Outcome: Interested — quoted ₹1,650/cylinder for 50 units', actor: 'Rahul Sharma', createdAt: '2026-10-03T09:15:00' },
  { id: 2, type: 'WhatsApp', title: 'WhatsApp to Mehta Oxygen', desc: 'Quotation reminder sent', actor: 'Rahul Sharma', createdAt: '2026-10-03T10:00:00' },
  { id: 3, type: 'Lead', title: 'Lead assigned to Rahul Sharma', desc: 'IndiaMART lead IM-8801 assigned', actor: 'Manager', createdAt: '2026-10-02T18:20:00' },
  { id: 4, type: 'IndiaMART', title: 'IndiaMART lead IM-8821 received', desc: 'Singh Welders — Oxygen Cylinders x 30', actor: 'System', createdAt: '2026-10-03T09:15:00' },
  { id: 5, type: 'Quotation', title: 'Quotation QT-2041 created', desc: 'For Sunita Devi — 20 Acetylene Cylinders, ₹62,000', actor: 'Amit Kumar', createdAt: '2026-10-02T14:30:00' },
  { id: 6, type: 'Opportunity', title: 'OPP-1041 moved to Negotiation', desc: 'Mehta Oxygen — ₹82,500 expected', actor: 'Rahul Sharma', createdAt: '2026-10-02T11:10:00' },
  { id: 7, type: 'Customer', title: 'Customer converted: Joshi Fabricators', desc: 'Lead LEAD-1006 converted to CTS customer', actor: 'Rahul Sharma', createdAt: '2026-10-01T16:45:00' },
  { id: 8, type: 'Follow-up', title: 'Follow-up completed for Rajesh Kumar', desc: 'Call back scheduled for Oct 4, 4 PM', actor: 'Priya Verma', createdAt: '2026-10-01T16:00:00' },
  { id: 9, type: 'Call', title: 'Call with Pooja Sharma', desc: 'Outcome: Not Interested — existing vendor till next quarter', actor: 'Sneha Patel', createdAt: '2026-10-02T16:30:00' },
  { id: 10, type: 'Campaign', title: 'Campaign "Diwali Greeting 2026" sent', desc: '120 recipients, 114 delivered', actor: 'System', createdAt: '2026-10-01T10:00:00' },
];

export default function CRMActivities() {
  const [activities, setActivities] = useState(fallbackActivities);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmDashboardApi.getRecentActivities({ size: 100 });
      if (Array.isArray(res.data)) setActivities(res.data);
      else if (res.data?.content?.length) setActivities(res.data.content);
    } catch { /* fallback */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const types = [...new Set(activities.map((a) => a.type).filter(Boolean))];
  const filtered = activities.filter((a) => {
    if (typeFilter && a.type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (a.title || '').toLowerCase().includes(q) || (a.desc || '').toLowerCase().includes(q) || (a.actor || '').toLowerCase().includes(q);
    }
    return true;
  });

  const counts = types.map((t) => ({ type: t, count: activities.filter((a) => a.type === t).length }));

  return (
    <div className="crm-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon"><FaHistory aria-hidden="true" /></div>
          <div>
            <h1 className="crm-title">Activities</h1>
            <p className="crm-subtitle">Full audit trail of CRM actions — calls, messages, status changes</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button className="crm-btn crm-btn-ghost" onClick={fetchAll}><FaSyncAlt aria-hidden="true" /> Refresh</button>
        </div>
      </div>

      <div className="crm-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
        {counts.map((c) => (
          <div className="crm-kpi-card" key={c.type} style={{ cursor: 'pointer', borderColor: typeFilter === c.type ? 'var(--primary)' : undefined }}
            onClick={() => setTypeFilter(typeFilter === c.type ? '' : c.type)}>
            <div><div className="crm-kpi-value" style={{ color: '#6366F1' }}>{c.count}</div><div className="crm-kpi-label">{c.type}</div></div>
          </div>
        ))}
      </div>

      <div className="crm-filters">
        <div className="crm-search-box">
          <FaSearch className="crm-search-icon" aria-hidden="true" />
          <input
            type="text"
            aria-label="Search activities"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search activities..."
          />
        </div>
        <div className="crm-status-filter" role="group" aria-label="Filter activities by type">
          <AppDropdown value={typeFilter} options={types.map((t) => ({ value: t, label: t }))}
            onChange={setTypeFilter} placeholder="All Types" menuWidth={200} menuClassName="crm-dropdown-window" />
        </div>
        <button className="crm-btn crm-btn-sm crm-btn-ghost" onClick={() => { setSearch(''); setTypeFilter(''); }}>Clear</button>
      </div>

      <div className="crm-panel-card">
        {loading && (
          <div className="crm-timeline">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="crm-timeline-item" key={`skeleton-${i}`}>
                <div className="crm-timeline-dot" style={{ background: 'var(--border)' }} />
                <SkeletonBlock width={120} height={12} radius={6} style={{ marginBottom: 8 }} />
                <SkeletonBlock width="60%" height={16} radius={6} style={{ marginBottom: 6 }} />
                <SkeletonBlock width="80%" height={12} radius={6} />
              </div>
            ))}
          </div>
        )}
        {!loading && filtered.length > 0 && <ActivityTimeline activities={filtered} />}
        {!loading && !filtered.length && (
          <div className="crm-empty">
            <div className="crm-empty-icon"><FaHistory aria-hidden="true" /></div>
            <div className="crm-empty-title">No activities found</div>
            <div className="crm-empty-desc">CRM activity will be recorded as you work with leads.</div>
          </div>
        )}
      </div>
    </div>
  );
}
